import { useWeb3React } from '@web3-react/core'
import BigNumber from 'bignumber.js'
import { getNftPoolConfigs } from 'config/constants/farms'
import { useGetContractAddress } from 'hooks/useContract'
import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { useAppDispatch } from 'state'
import { fetchNftPoolFarmDataAsync } from 'state/xFarms'
import { fetchUserXTokenDataAsync } from 'state/xToken'
import { useXTokenInfo } from 'state/xToken/hooks'
import { IYieldBoosterInfo, UserXTokenInfo } from 'state/xToken/types'

export enum BoostType {
  BOOST,
  UNBOOST,
}

export interface YieldBoosterContextType {
  data: IYieldBoosterInfo
  userInfo: UserXTokenInfo
  yieldBoosterAddress: string
  hasApprovalForAmount: (amt: string) => boolean
  boostViewType: BoostType
  setBoostViewType: (view: number) => void
}

export const YieldBoosterContext = createContext<YieldBoosterContextType | null>(null)

export function YieldBoosterProvider({ children }: { children: ReactNode }) {
  const [boostViewType, setBoostViewType] = useState(BoostType.BOOST)

  const { userInfo, yieldBoosterInfo } = useXTokenInfo()
  const yieldBoosterAddress = useGetContractAddress('yieldBooster')
  const dispatch = useAppDispatch()
  const { account, chainId, provider } = useWeb3React()

  useEffect(() => {
    dispatch(fetchUserXTokenDataAsync({ account, chainId, provider }))
    dispatch(fetchNftPoolFarmDataAsync({ chainId, farmConfigs: getNftPoolConfigs(chainId), provider }))
  }, [account, chainId, provider])

  function hasApprovalForAmount(amount: string) {
    const amt = new BigNumber(amount || 0)
    if (amt.isZero()) return false
    return new BigNumber(userInfo.yieldBoosterInfo.approvalAmount).gte(amt)
  }

  return (
    <YieldBoosterContext.Provider
      value={{
        data: yieldBoosterInfo,
        userInfo: {
          ...userInfo,
          xTokenBalance: new BigNumber(userInfo?.xTokenBalance || 0).div(1e18).toString(),
        },
        yieldBoosterAddress,
        hasApprovalForAmount,
        boostViewType,
        setBoostViewType,
      }}
    >
      {children}
    </YieldBoosterContext.Provider>
  )
}

export function useYieldBooster() {
  return useContext(YieldBoosterContext) as YieldBoosterContextType
}
